import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetHomepageSettingsQuery,
  useUpdateHomepageSettingsMutation,
} from "../redux/services/api";
import { setSettings as setReduxSettings } from "../redux/slices/homepageSlice";
import { selectUser, selectIsAuthenticated } from "../redux/slices/authSlice";
import ImageUpload from "../components/ImageUpload";
import Icon from "../components/Icon";
import RichTextEditor from "../components/RichTextEditor";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const HomepageSettings = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();

  // RTK Query hooks
  const {
    data: settingsResponse,
    isLoading: loadingSettings,
    refetch,
  } = useGetHomepageSettingsQuery();
  const [updateSettings, { isLoading: saving }] =
    useUpdateHomepageSettingsMutation();

  // Check authentication status
  const [settings, setSettings] = useState({
    branding: {
      logo: "",
      companyName: "Silver Fort",
      companyNameAr: "",
      tagline: "Leading the future of business solutions",
      taglineAr: "",
      heroBackground: "",
    },
    socialMedia: {
      facebook: "",
      twitter: "",
      linkedin: "",
      instagram: "",
      youtube: "",
      tiktok: "",
      snapchat: "",
      pinterest: "",
      telegram: "",
      whatsapp: "",
      discord: "",
    },
    contact: {
      email: "info@silverfort.com",
      phone: "+1 (555) 123-4567",
      address: "",
      addressAr: "",
      addressLink: "",
      whatsapp: "",
      whatsappDefaultMessage: "",
      footerParagraph:
        "© 2024 Silver Fort. All rights reserved. Professional solutions for your business needs.",
      footerParagraphAr: "",
    },
    aboutUs: {
      title: "About Us",
      titleAr: "",
      description:
        "We are a leading company dedicated to providing innovative solutions and exceptional service to our clients worldwide.",
      descriptionAr: "",
      images: [],
    },
    heroSlider: [],
    headTags: {
      headerTags: "",
      footerTags: "",
    },
  });

  // Update document title with Company Name and Test Tagline
  useEffect(() => {
    const companyName = settings.branding?.companyName || "Silver Fort";
    const tagline = settings.branding?.tagline || "Professional Solutions";
    document.title = `${companyName} - ${tagline} | Settings`;
  }, [settings.branding?.companyName, settings.branding?.tagline]);

  const [activeTab, setActiveTab] = useState("branding");
  const [saveStatus, setSaveStatus] = useState("");
  const messageType = useMessageType(saveStatus);

  const loading = loadingSettings;

  // Load settings from Redux when fetched
  useEffect(() => {
    if (
      settingsResponse &&
      settingsResponse.success &&
      settingsResponse.settings
    ) {
      const cleanedSettings = {
        ...settingsResponse.settings,
        aboutUs: {
          ...settingsResponse.settings.aboutUs,
          images: (settingsResponse.settings.aboutUs?.images || []).filter(
            (img) => img && img !== ""
          ),
        },
        contact: {
          ...settingsResponse.settings.contact,
          addressLink: settingsResponse.settings.contact?.addressLink || "",
          whatsappDefaultMessage: settingsResponse.settings.contact?.whatsappDefaultMessage || "",
          footerParagraph: settingsResponse.settings.footer?.paragraph || "",
          footerParagraphAr:
            settingsResponse.settings.footer?.paragraphAr || "",
        },
        heroSlider: settingsResponse.settings.heroSlider || [],
        headTags: settingsResponse.settings.headTags || {
          headerTags: "",
          footerTags: "",
        },
      };
      setSettings(cleanedSettings);
      dispatch(setReduxSettings(cleanedSettings));
      console.log("Settings loaded from Redux");
    }
  }, [settingsResponse, dispatch]);

  const handleInputChange = (section, field, value) => {
    if (section === "heroSlider" && field === "") {
      // Special case for heroSlider array
      setSettings((prev) => ({
        ...prev,
        heroSlider: value,
      }));
    } else {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    }
  };

  const handleImageChange = (section, field, imageUrl) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: imageUrl,
      },
    }));
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      setSaveStatus(t("homepageSettings.pleaseLoginToSave"));
      return;
    }

    setSaveStatus("");

    try {
      console.log("Saving settings:", settings);

      // Create FormData for file uploads
      const formData = new FormData();

      // Add all non-file data
      formData.append(
        "branding[companyName]",
        settings.branding.companyName || ""
      );
      formData.append(
        "branding[companyNameAr]",
        settings.branding.companyNameAr || ""
      );
      formData.append("branding[tagline]", settings.branding.tagline || "");
      formData.append("branding[taglineAr]", settings.branding.taglineAr || "");
      formData.append("contact[email]", settings.contact.email || "");
      formData.append("contact[phone]", settings.contact.phone || "");
      formData.append("contact[whatsapp]", settings.contact.whatsapp || "");
      formData.append("contact[whatsappDefaultMessage]", settings.contact.whatsappDefaultMessage || "");
      formData.append(
        "footer[paragraph]",
        settings.contact.footerParagraph || ""
      );
      formData.append(
        "footer[paragraphAr]",
        settings.contact.footerParagraphAr || ""
      );
      formData.append("contact[address]", settings.contact.address || "");
      formData.append("contact[addressAr]", settings.contact.addressAr || "");
      formData.append(
        "contact[addressLink]",
        settings.contact.addressLink || ""
      );

      // Add social media URLs
      Object.keys(settings.socialMedia).forEach((key) => {
        formData.append(`socialMedia[${key}]`, settings.socialMedia[key] || "");
      });

      // Add about us data
      formData.append("aboutUs[title]", settings.aboutUs.title || "");
      formData.append("aboutUs[titleAr]", settings.aboutUs.titleAr || "");
      formData.append(
        "aboutUs[description]",
        settings.aboutUs.description || ""
      );
      formData.append(
        "aboutUs[descriptionAr]",
        settings.aboutUs.descriptionAr || ""
      );

      // Handle about us images - only send non-empty images
      const validImages = settings.aboutUs.images.filter(
        (image) => image instanceof File || (image && image !== "")
      );

      console.log("About Us images:", settings.aboutUs.images);
      console.log("Valid images:", validImages);

      // Handle About Us images - always send existing URLs, only send new files if they exist
      const newImageFiles = validImages.filter((img) => img instanceof File);
      const existingImageUrls = validImages.filter(
        (img) => typeof img === "string" && img !== ""
      );

      // Always send existing image URLs to preserve them (prevents deletion on update)
      // Even if there are no new files, we need to preserve existing images
      if (existingImageUrls.length > 0) {
        formData.append(
          "aboutUs[existing_images]",
          JSON.stringify(existingImageUrls)
        );
        console.log(
          "Sending existing images to preserve them:",
          existingImageUrls
        );
      } else if (settings.aboutUs.images.length === 0) {
        // If no images at all, send empty array to clear images
        formData.append("aboutUs[existing_images]", JSON.stringify([]));
        console.log("No images found, sending empty array to clear images");
      }

      // Only send new files if there are any
      if (newImageFiles.length > 0) {
        newImageFiles.forEach((image, index) => {
          formData.append(`aboutUs[images][${index}]`, image);
        });
      }

      // Handle hero slider
      if (settings.heroSlider && settings.heroSlider.length > 0) {
        settings.heroSlider.forEach((slide, index) => {
          formData.append(`heroSlider[${index}][title]`, slide.title || "");
          formData.append(`heroSlider[${index}][titleAr]`, slide.titleAr || "");
          formData.append(
            `heroSlider[${index}][description]`,
            slide.description || ""
          );
          formData.append(
            `heroSlider[${index}][descriptionAr]`,
            slide.descriptionAr || ""
          );
          formData.append(
            `heroSlider[${index}][buttonText]`,
            slide.buttonText || ""
          );
          formData.append(
            `heroSlider[${index}][buttonTextAr]`,
            slide.buttonTextAr || ""
          );
          formData.append(
            `heroSlider[${index}][buttonLink]`,
            slide.buttonLink || ""
          );

          // Handle existing image URLs
          if (
            slide.image &&
            typeof slide.image === "string" &&
            slide.image !== ""
          ) {
            formData.append(
              `heroSlider[${index}][existing_image]`,
              slide.image
            );
          }

          // Handle new image file
          if (slide.image instanceof File) {
            formData.append(`heroSlider[${index}][image]`, slide.image);
          }
        });
      } else {
        // Send empty array to clear all slides
        formData.append("heroSlider", JSON.stringify([]));
      }

      // Debug: Log what's being sent for About Us images
      console.log("About Us images being sent:", validImages.length);
      console.log("New image files:", newImageFiles.length);
      console.log("Existing image URLs:", existingImageUrls.length);
      console.log("Preserving existing images:", existingImageUrls.length > 0);
      if (validImages.length > 0) {
        console.log(
          "About Us images details:",
          validImages.map((img, idx) => ({
            index: idx,
            type: img instanceof File ? "File" : typeof img,
            value: img instanceof File ? img.name : img,
          }))
        );
      }

      // Final FormData debug
      console.log("Final FormData contents:");
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      // Check if there are any About Us images being sent
      const aboutUsImagesInFormData = [];
      for (let [key, value] of formData.entries()) {
        if (key.startsWith("aboutUs[images]")) {
          aboutUsImagesInFormData.push({ key, value });
        }
      }
      console.log("About Us images in FormData:", aboutUsImagesInFormData);

      // If there are no valid images, we should not send any About Us images
      if (validImages.length === 0) {
        console.log(
          "No valid About Us images found, not sending any About Us images"
        );
      }

      // Check if the About Us images array contains empty strings
      const emptyStringsInImages = settings.aboutUs.images.filter(
        (img) => img === ""
      );
      if (emptyStringsInImages.length > 0) {
        console.log(
          "Warning: Found empty strings in About Us images array:",
          emptyStringsInImages.length
        );
        console.log("This might cause validation errors on the backend");
      }

      // Handle head tags
      if (settings.headTags) {
        formData.append(
          "headTags[headerTags]",
          settings.headTags.headerTags || ""
        );
        formData.append(
          "headTags[footerTags]",
          settings.headTags.footerTags || ""
        );
      }

      // Handle file uploads - only send if they are actual files
      if (settings.branding.logo instanceof File) {
        formData.append("branding[logo]", settings.branding.logo);
      }
      // Don't send empty strings for logo

      if (settings.branding.heroBackground instanceof File) {
        formData.append(
          "branding[heroBackground]",
          settings.branding.heroBackground
        );
      }
      // Save with Redux mutation
      const result = await updateSettings(formData).unwrap();
      console.log("Save response:", result);

      if (result.success) {
        setSaveStatus(t("homepageSettings.settingsSavedSuccessfully"));
        // Update Redux store with new settings
        if (result.settings) {
          const updatedSettings = {
            ...settings,
            ...result.settings,
          };
          setSettings(updatedSettings);
          dispatch(setReduxSettings(updatedSettings));
        }
        refetch(); // Refetch to ensure we have latest data
        setTimeout(() => setSaveStatus(""), 3000);
      } else {
        setSaveStatus(`Error: ${result.message || "Failed to save settings"}`);
      }
    } catch (error) {
      console.error("Save error:", error);
      setSaveStatus(
        error.data?.message || t("homepageSettings.errorSavingSettings")
      );
    }
  };

  const tabs = [
    {
      id: "branding",
      label: t("homepageSettings.logoAndBranding"),
      icon: "image",
    },
    {
      id: "heroSlider",
      label: t("homepageSettings.heroSlider"),
      icon: "image",
    },
    { id: "social", label: t("homepageSettings.socialMedia"), icon: "users" },
    { id: "contact", label: t("homepageSettings.contactInfo"), icon: "phone" },
    { id: "about", label: t("homepageSettings.aboutUs"), icon: "info-circle" },
    { id: "headTags", label: t("homepageSettings.headTags"), icon: "code" },
  ];

  if (loading) {
    return (
      <div className="homepage-settings">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("homepageSettings.loadingSettings")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="homepage-settings">
        <div className="auth-required">
          <h2>{t("homepageSettings.authenticationRequired")}</h2>
          <p>{t("homepageSettings.pleaseLoginToAccess")}</p>
          <button
            className="login-btn"
            onClick={() => (window.location.href = "/login")}
          >
            {t("homepageSettings.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  // Additional safeguard - check localStorage as well
  const hasLocalUser = localStorage.getItem("user");
  if (!user || !isAuthenticated || !hasLocalUser) {
    return (
      <div className="homepage-settings">
        <div className="auth-required">
          <h2>{t("homepageSettings.authenticationRequired")}</h2>
          <p>{t("homepageSettings.pleaseLoginToAccess")}</p>
          <button
            className="login-btn"
            onClick={() => (window.location.href = "/login")}
          >
            {t("homepageSettings.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage-settings">
      <div className="settings-header">
        <div className="header-content">
          <h1>{t("homepageSettings.title")}</h1>
          <p>{t("homepageSettings.subtitle")}</p>
        </div>
        <div className="header-actions">
          <button
            className="preview-btn"
            onClick={() => window.open("/", "_blank")}
          >
            <Icon name="eye" className="btn-icon" size="16px" />
            {t("homepageSettings.previewHomepage")}
          </button>
          <button
            className={`save-btn ${saving ? "saving" : ""}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="btn-spinner"></span>
                {t("homepageSettings.saving")}
              </>
            ) : (
              <>
                <Icon name="save" className="btn-icon" size="16px" />
                {t("homepageSettings.saveChanges")}
              </>
            )}
          </button>
        </div>
      </div>

      <StatusMessage
        message={saveStatus}
        type={messageType}
        show={!!saveStatus}
        autoClose={messageType === "success"}
        autoCloseDelay={5000}
        onClose={() => setSaveStatus("")}
      />
      {saveStatus && saveStatus.includes("Session expired") && (
        <div style={{ marginTop: "10px", textAlign: "center" }}>
          <button
            className="login-btn"
            onClick={() => (window.location.href = "/login")}
          >
            {t("homepageSettings.logIn")}
          </button>
        </div>
      )}

      <div className="settings-container">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`nav-item ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon name={tab.icon} className="nav-icon" size="18px" />
                <span className="nav-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="settings-content">
          {activeTab === "heroSlider" && (
            <div className="settings-panel">
              <div className="panel-header">
                <h2>{t("homepageSettings.heroSlider")}</h2>
                <p>{t("homepageSettings.manageYourHeroSlider")}</p>
              </div>

              <div className="form-grid">
                {(settings.heroSlider || []).map((slide, index) => (
                  <div key={index} className="slide-item full-width">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem",
                      }}
                    >
                      <h3 style={{ margin: 0, color: "#fff" }}>
                        Slide {index + 1}
                      </h3>
                      <button
                        type="button"
                        className="remove-slide-btn"
                        onClick={() => {
                          const newSlides = (settings.heroSlider || []).filter(
                            (_, i) => i !== index
                          );
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                      >
                        <Icon name="trash" size="16px" />
                        {t("homepageSettings.removeSlide")}
                      </button>
                    </div>

                    <div className="form-group">
                      <ImageUpload
                        label={t("homepageSettings.slideImage")}
                        value={slide.image}
                        onChange={(file) => {
                          const newSlides = [...(settings.heroSlider || [])];
                          newSlides[index] = {
                            ...newSlides[index],
                            image: file,
                          };
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                        className="image-upload-modern"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("homepageSettings.slideTitle")}</label>
                      <input
                        type="text"
                        value={slide.title || ""}
                        onChange={(e) => {
                          const newSlides = [...(settings.heroSlider || [])];
                          newSlides[index] = {
                            ...newSlides[index],
                            title: e.target.value,
                          };
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                        placeholder="Enter slide title"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("homepageSettings.slideTitleArabic")}</label>
                      <input
                        type="text"
                        value={slide.titleAr || ""}
                        onChange={(e) => {
                          const newSlides = [...(settings.heroSlider || [])];
                          newSlides[index] = {
                            ...newSlides[index],
                            titleAr: e.target.value,
                          };
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                        placeholder="أدخل عنوان الشريحة"
                        className="form-input"
                        dir="rtl"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("homepageSettings.slideDescription")}</label>
                      <textarea
                        value={slide.description || ""}
                        onChange={(e) => {
                          const newSlides = [...(settings.heroSlider || [])];
                          newSlides[index] = {
                            ...newSlides[index],
                            description: e.target.value,
                          };
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                        placeholder="Enter slide description"
                        className="form-textarea"
                        rows="3"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        {t("homepageSettings.slideDescriptionArabic")}
                      </label>
                      <textarea
                        value={slide.descriptionAr || ""}
                        onChange={(e) => {
                          const newSlides = [...(settings.heroSlider || [])];
                          newSlides[index] = {
                            ...newSlides[index],
                            descriptionAr: e.target.value,
                          };
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                        placeholder="أدخل وصف الشريحة"
                        className="form-textarea"
                        rows="3"
                        dir="rtl"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("homepageSettings.buttonText")}</label>
                      <input
                        type="text"
                        value={slide.buttonText || ""}
                        onChange={(e) => {
                          const newSlides = [...(settings.heroSlider || [])];
                          newSlides[index] = {
                            ...newSlides[index],
                            buttonText: e.target.value,
                          };
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                        placeholder="Enter button text"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("homepageSettings.buttonTextArabic")}</label>
                      <input
                        type="text"
                        value={slide.buttonTextAr || ""}
                        onChange={(e) => {
                          const newSlides = [...(settings.heroSlider || [])];
                          newSlides[index] = {
                            ...newSlides[index],
                            buttonTextAr: e.target.value,
                          };
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                        placeholder="أدخل نص الزر"
                        className="form-input"
                        dir="rtl"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("homepageSettings.buttonLink")}</label>
                      <input
                        type="url"
                        value={slide.buttonLink || ""}
                        onChange={(e) => {
                          const newSlides = [...(settings.heroSlider || [])];
                          newSlides[index] = {
                            ...newSlides[index],
                            buttonLink: e.target.value,
                          };
                          handleInputChange("heroSlider", "", newSlides);
                        }}
                        placeholder="https://example.com"
                        className="form-input"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-group add-slide-container">
                <button
                  type="button"
                  className="add-slide-btn"
                  onClick={() => {
                    const newSlides = [
                      ...(settings.heroSlider || []),
                      {
                        image: "",
                        title: "",
                        titleAr: "",
                        description: "",
                        descriptionAr: "",
                        buttonText: "",
                        buttonTextAr: "",
                        buttonLink: "",
                      },
                    ];
                    handleInputChange("heroSlider", "", newSlides);
                  }}
                >
                  <Icon name="plus" size="16px" />
                  {t("homepageSettings.addSlide")}
                </button>
              </div>
            </div>
          )}

          {activeTab === "branding" && (
            <div className="settings-panel">
              <div className="panel-header">
                <h2>{t("homepageSettings.logoAndBranding")}</h2>
                <p>{t("homepageSettings.uploadAndManageBrandAssets")}</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <ImageUpload
                    label={t("homepageSettings.uploadImage")}
                    value={settings.branding.logo}
                    onChange={(file) =>
                      handleInputChange("branding", "logo", file)
                    }
                    className="image-upload-modern"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.companyName")}</label>
                  <input
                    type="text"
                    value={settings.branding.companyName}
                    onChange={(e) =>
                      handleInputChange(
                        "branding",
                        "companyName",
                        e.target.value
                      )
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterCompanyName"
                    )}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.companyNameArabic")}</label>
                  <input
                    type="text"
                    value={settings.branding.companyNameAr}
                    onChange={(e) =>
                      handleInputChange(
                        "branding",
                        "companyNameAr",
                        e.target.value
                      )
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterCompanyNameArabic"
                    )}
                    className="form-input"
                    dir="rtl"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.tagline")}</label>
                  <input
                    type="text"
                    value={settings.branding.tagline}
                    onChange={(e) =>
                      handleInputChange("branding", "tagline", e.target.value)
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterCompanyTagline"
                    )}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.taglineArabic")}</label>
                  <input
                    type="text"
                    value={settings.branding.taglineAr}
                    onChange={(e) =>
                      handleInputChange("branding", "taglineAr", e.target.value)
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterCompanyTaglineArabic"
                    )}
                    className="form-input"
                    dir="rtl"
                  />
                </div>

                <div className="form-group">
                  <ImageUpload
                    label={t("homepageSettings.heroBackground")}
                    value={settings.branding.heroBackground}
                    onChange={(file) =>
                      handleInputChange("branding", "heroBackground", file)
                    }
                    className="image-upload-modern"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "social" && (
            <div className="settings-panel">
              <div className="panel-header">
                <h2>{t("homepageSettings.socialMedia")}</h2>
                <p>{t("homepageSettings.manageYourSocialMediaLinks")}</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>{t("homepageSettings.facebook")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.facebook}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "facebook",
                        e.target.value
                      )
                    }
                    placeholder="https://facebook.com/yourpage"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.twitter")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.twitter}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "twitter",
                        e.target.value
                      )
                    }
                    placeholder="https://twitter.com/yourhandle"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.linkedin")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.linkedin}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "linkedin",
                        e.target.value
                      )
                    }
                    placeholder="https://linkedin.com/company/yourcompany"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.instagram")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.instagram}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "instagram",
                        e.target.value
                      )
                    }
                    placeholder="https://instagram.com/yourhandle"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.youtube")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.youtube}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "youtube",
                        e.target.value
                      )
                    }
                    placeholder="https://youtube.com/yourchannel"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.tiktok")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.tiktok}
                    onChange={(e) =>
                      handleInputChange("socialMedia", "tiktok", e.target.value)
                    }
                    placeholder="https://tiktok.com/@yourhandle"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.snapchat")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.snapchat}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "snapchat",
                        e.target.value
                      )
                    }
                    placeholder="https://snapchat.com/add/yourhandle"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.pinterest")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.pinterest}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "pinterest",
                        e.target.value
                      )
                    }
                    placeholder="https://pinterest.com/yourhandle"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.telegram")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.telegram}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "telegram",
                        e.target.value
                      )
                    }
                    placeholder="https://t.me/yourhandle"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.discord")}</label>
                  <input
                    type="url"
                    value={settings.socialMedia.discord}
                    onChange={(e) =>
                      handleInputChange(
                        "socialMedia",
                        "discord",
                        e.target.value
                      )
                    }
                    placeholder="https://discord.gg/yourserver"
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "contact" && (
            <div className="settings-panel">
              <div className="panel-header">
                <h2>{t("homepageSettings.contactInfo")}</h2>
                <p>{t("homepageSettings.manageYourContactInformation")}</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>{t("homepageSettings.email")}</label>
                  <input
                    type="email"
                    value={settings.contact.email}
                    onChange={(e) =>
                      handleInputChange("contact", "email", e.target.value)
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterEmailAddress"
                    )}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.phone")}</label>
                  <input
                    type="tel"
                    value={settings.contact.phone}
                    onChange={(e) =>
                      handleInputChange("contact", "phone", e.target.value)
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterPhoneNumber"
                    )}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.whatsapp")}</label>
                  <input
                    type="url"
                    value={settings.contact.whatsapp}
                    onChange={(e) =>
                      handleInputChange("contact", "whatsapp", e.target.value)
                    }
                    placeholder="https://wa.me/yournumber"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.whatsappDefaultMessage")}</label>
                  <textarea
                    value={settings.contact.whatsappDefaultMessage}
                    onChange={(e) =>
                      handleInputChange(
                        "contact",
                        "whatsappDefaultMessage",
                        e.target.value
                      )
                    }
                    placeholder={t("homepageSettings.enterWhatsAppDefaultMessage")}
                    className="form-input"
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.footerParagraph")}</label>
                  <textarea
                    value={settings.contact.footerParagraph}
                    onChange={(e) =>
                      handleInputChange(
                        "contact",
                        "footerParagraph",
                        e.target.value
                      )
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterFooterText"
                    )}
                    className="form-textarea"
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.footerParagraphArabic")}</label>
                  <textarea
                    value={settings.contact.footerParagraphAr}
                    onChange={(e) =>
                      handleInputChange(
                        "contact",
                        "footerParagraphAr",
                        e.target.value
                      )
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterFooterTextArabic"
                    )}
                    className="form-textarea"
                    rows="2"
                    dir="rtl"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.address")}</label>
                  <textarea
                    value={settings.contact.address}
                    onChange={(e) =>
                      handleInputChange("contact", "address", e.target.value)
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterFullAddress"
                    )}
                    className="form-textarea"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.addressArabic")}</label>
                  <textarea
                    value={settings.contact.addressAr}
                    onChange={(e) =>
                      handleInputChange("contact", "addressAr", e.target.value)
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterFullAddressArabic"
                    )}
                    className="form-textarea"
                    rows="3"
                    dir="rtl"
                  />
                </div>

                <div className="form-group">
                  <label>
                    {t("homepageSettings.addressLink") || "Address Link"}
                  </label>
                  <input
                    type="url"
                    value={settings.contact.addressLink}
                    onChange={(e) =>
                      handleInputChange(
                        "contact",
                        "addressLink",
                        e.target.value
                      )
                    }
                    placeholder="https://maps.google.com/..."
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "about" && (
            <div className="settings-panel">
              <div className="panel-header">
                <h2>{t("homepageSettings.aboutUs")}</h2>
                <p>{t("homepageSettings.manageYourAboutUsContent")}</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>{t("homepageSettings.aboutContent")}</label>
                  <input
                    type="text"
                    value={settings.aboutUs.title}
                    onChange={(e) =>
                      handleInputChange("aboutUs", "title", e.target.value)
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterAboutUsTitle"
                    )}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.aboutContentArabic")}</label>
                  <input
                    type="text"
                    value={settings.aboutUs.titleAr}
                    onChange={(e) =>
                      handleInputChange("aboutUs", "titleAr", e.target.value)
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterAboutUsTitleArabic"
                    )}
                    className="form-input"
                    dir="rtl"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.aboutContent")}</label>
                  <textarea
                    value={settings.aboutUs.description}
                    onChange={(e) =>
                      handleInputChange(
                        "aboutUs",
                        "description",
                        e.target.value
                      )
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterAboutUsDescription"
                    )}
                    className="form-textarea"
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label>{t("homepageSettings.aboutContentArabic")}</label>
                  <textarea
                    value={settings.aboutUs.descriptionAr}
                    onChange={(e) =>
                      handleInputChange(
                        "aboutUs",
                        "descriptionAr",
                        e.target.value
                      )
                    }
                    placeholder={t(
                      "homepageSettings.placeholderEnterAboutUsDescriptionArabic"
                    )}
                    className="form-textarea"
                    rows="4"
                    dir="rtl"
                  />
                </div>

                <div className="form-group full-width">
                  <label>{t("homepageSettings.uploadImage")}</label>
                  <div className="multi-image-upload">
                    {settings.aboutUs.images.map((image, index) => (
                      <div key={index} className="image-upload-item">
                        <ImageUpload
                          label={`${t("homepageSettings.uploadImage")} ${
                            index + 1
                          }`}
                          value={image}
                          onChange={(file) => {
                            const newImages = [...settings.aboutUs.images];
                            newImages[index] = file;
                            handleInputChange("aboutUs", "images", newImages);
                          }}
                          onRemove={() => {
                            const newImages = settings.aboutUs.images.filter(
                              (_, i) => i !== index
                            );
                            handleInputChange("aboutUs", "images", newImages);
                          }}
                          className="image-upload-modern"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="add-image-btn"
                      onClick={() => {
                        const newImages = [...settings.aboutUs.images, ""];
                        handleInputChange("aboutUs", "images", newImages);
                      }}
                    >
                      <Icon name="plus" size="16px" />
                      {t("homepageSettings.addImage")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "headTags" && (
            <div className="settings-panel">
              <div className="panel-header">
                <h2>{t("homepageSettings.headTags")}</h2>
                <p>{t("homepageSettings.manageHeadTags")}</p>
        </div>

              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Header Tags (Code to insert in &lt;head&gt;)</label>
                  <textarea
                    value={settings.headTags?.headerTags || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "headTags",
                        "headerTags",
                        e.target.value
                      )
                    }
                    placeholder='<meta charset="UTF-8">&#10;<meta name="viewport" content="width=device-width, initial-scale=1.0">&#10;<title>Your Title</title>'
                    className="form-textarea"
                    rows="10"
                    style={{ fontFamily: "monospace" }}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Footer Tags (Code to insert in footer)</label>
                  <textarea
                    value={settings.headTags?.footerTags || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "headTags",
                        "footerTags",
                        e.target.value
                      )
                    }
                    placeholder='<script src="analytics.js"></script>&#10;<link rel="stylesheet" href="custom.css">'
                    className="form-textarea"
                    rows="10"
                    style={{ fontFamily: "monospace" }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomepageSettings;
